import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';

import Kpi from 'types/Kpi';
import api from 'utils/api';
import uuid4 from 'uuid4';

import { DirtyCell, QueueItem, SavingStatus } from '../types';

interface Params {
    companyId: number;
    assessmentId: number;
    setKpis: Dispatch<SetStateAction<Kpi[]>>;
}

const useKpiSavingQueue = (params: Params) => {
    const { companyId, assessmentId, setKpis } = params;

    const [savingQueue, setSavingQueue] = useState<QueueItem[]>([]);
    const [dirtyCell, setDirtyCell] = useState<DirtyCell | null>(null);

    const pushDataToQueue = useCallback((kpiId: number, data: Kpi) => {
        setSavingQueue(currentQueue => {
            return [...currentQueue, {
                id: uuid4(),
                kpiId,
                data,
                status: 'waiting'
            }];
        });
    }, [setSavingQueue]);

    const savingStatus = useMemo((): SavingStatus => {
        if (dirtyCell) {
            return 'pending';
        }

        const kpiStatus = savingQueue.reduce((prev: any, curr: any) => {
            prev[curr.kpiId] = curr.status;

            return prev;
        }, {});

        const pending = Object.entries(kpiStatus).filter((k: any) => k[1] === 'waiting' || k[1] === 'running');
        const failed = Object.entries(kpiStatus).filter((k: any) => k[1] === 'failed');

        if (pending.length > 0) {
            return 'pending';
        } else if (failed.length > 0) {
            return 'failed';
        } else {
            return 'success';
        }
    }, [dirtyCell, savingQueue]);

    const retrySave = useCallback(() => {
        const kpiStatus = savingQueue.reduce((prev: any, curr: any) => {
            prev[curr.kpiId] = curr.status;

            return prev;
        }, {});

        const failed = Object.entries(kpiStatus).filter((k: any) => k[1] === 'failed');

        failed.forEach((f: any) => {
            const index = f[0];

            const { data } = savingQueue.findLast(q => q.kpiId === parseInt(index, 10));

            setSavingQueue((s: any[]) => {
                return [...s, {
                    id: uuid4(),
                    kpiId: index,
                    data,
                    status: 'waiting'
                }];
            });
        });
    }, [savingQueue]);

    useEffect(() => {
        const firstRunning = savingQueue.find(q => q.status === 'running');
        const firstWaiting = savingQueue.find(q => q.status === 'waiting');

        if (!firstRunning && firstWaiting) {
            setSavingQueue(q => {
                const clonedQ = [...q];
                const firstWaitingIndex = savingQueue.findIndex((q2: any) => q2.id === firstWaiting.id);

                clonedQ[firstWaitingIndex].status = 'running';

                return clonedQ;
            });

            api.request('/companies/' + companyId + '/plastic-assessments/' + assessmentId + '/kpis/' + (firstWaiting.kpiId === 0 ? '' : firstWaiting.kpiId), (firstWaiting.kpiId === 0 ? 'POST' : 'PUT'), firstWaiting.data).then((res) => {
                setSavingQueue(q => {
                    const clonedQ = [...q];
                    const firstWaitingIndex = savingQueue.findIndex((q2: any) => q2.id === firstWaiting.id);

                    clonedQ[firstWaitingIndex].status = 'success';

                    return clonedQ;
                });

                setKpis(data => {
                    const clonedData = [...data];
                    const realIndex = clonedData.findIndex(k => k.id === firstWaiting.kpiId);
                    const clonedKpi = { ...clonedData[realIndex] };

                    clonedKpi.averageUsage = res.averageUsage;
                    clonedKpi.co2Saved = res.co2Saved;
                    clonedKpi.co2SavedUpdatedAt = res.co2SavedUpdatedAt;
                    clonedKpi.lastMonthUsage = res.lastMonthUsage;

                    clonedData[realIndex] = clonedKpi;
                    return clonedData;
                });
            }).catch(() => {
                setSavingQueue(q => {
                    const clonedQ = [...q];
                    const firstWaitingIndex = savingQueue.findIndex((q2: any) => q2.id === firstWaiting.id);

                    clonedQ[firstWaitingIndex].status = 'failed';

                    return clonedQ;
                });
            });
        }
    }, [savingQueue, assessmentId, companyId, setKpis]);

    return { pushDataToQueue, savingStatus, retrySave, setDirtyCell };
};

export default useKpiSavingQueue;